import { useState } from 'react'
import { sanitize } from 'dompurify'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import {
  ActionElementType,
  confidenceLevels,
  priorityGoals,
} from 'Components/SharedComponents/Surveys/constants'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import {
  ButtonAction,
  CustomAction,
  GoalToCreate,
  HandleGoalsArgs,
  Priorities,
} from 'Components/SharedComponents/Surveys/types'
import { GoalCategory } from 'Generated/graphql-types'
import { snakeCaseToSpaces } from 'Utils/formatters'
import { HandleActionArgs } from '../types'
import { CheckInWizardStep } from '../constants'
import { CheckInSurveyStepper } from './CheckInSurveyStepper'
import {
  additionalPriorityCategories,
  initialPriorities,
  priorityCategories,
} from '../../NeedsAssessment/constants'
import { ConfidenceLevel } from '../../NeedsAssessment/types'

type RevisitYourBaselinesProps = {
  onAnyAction: ({ nextStep, callback }: HandleActionArgs) => void
  onSetPriorities: (priorities: Priorities) => void
  onToggleTemplateGoal: (args: HandleGoalsArgs) => GoalToCreate[]
  priorities: Priorities
}

export const RevisitYourBaselines = ({
  onAnyAction,
  onSetPriorities,
  onToggleTemplateGoal,
  priorities,
}: RevisitYourBaselinesProps) => {
  const [currentSubStep, setCurrentSubStep] = useState(0)

  // Remove the "my priority is not listed" choice from the Needs Assessment options
  const priorityCategoryOptions = [
    ...priorityCategories.slice(0, -1),
    ...additionalPriorityCategories,
  ].sort((a, b) => a.label.localeCompare(b.label))
  const isFirstSubStep = currentSubStep === 0
  const isLastSubStep = currentSubStep === 2
  const { firstPriority, secondPriority, thirdPriority, missingPriority } =
    priorities
  const firstPriorityGoals = priorityGoals[firstPriority.category] || []
  const secondPriorityGoals = priorityGoals[secondPriority.category] || []
  const thirdPriorityGoals = priorityGoals[thirdPriority?.category] || []

  const {
    category: firstCategory,
    challenges: firstPriorityChallenges,
    confidenceLevel: firstPriorityConfidence,
    updateNotes: firstPriorityUpdateNotes,
  } = firstPriority

  const {
    category: secondCategory,
    challenges: secondPriorityChallenges,
    confidenceLevel: secondPriorityConfidence,
    updateNotes: secondPriorityUpdateNotes,
  } = secondPriority

  const {
    category: thirdCategory,
    challenges: thirdPriorityChallenges,
    confidenceLevel: thirdPriorityConfidence,
    updateNotes: thirdPriorityUpdateNotes,
  } = thirdPriority || {} // Third priority is optional

  const {
    category: missingPriorityCategory,
    challenges: missingPriorityChallenges,
    confidenceLevel: missingPriorityConfidence,
    customPriorities: missingPriorityCustomPriorities,
  } = missingPriority

  const firstCategoryLabel = snakeCaseToSpaces(firstCategory, true)
  const secondCategoryLabel = snakeCaseToSpaces(secondCategory, true)
  const thirdCategoryLabel = snakeCaseToSpaces(thirdCategory, true)

  const checkIfNextIsDisabled = () => {
    if (currentSubStep === 0) {
      if (missingPriorityCategory)
        return !missingPriorityConfidence || !missingPriorityCustomPriorities
      return !firstCategory || !firstPriorityConfidence
    }

    if (currentSubStep === 1)
      return !secondCategory || !secondPriorityConfidence

    if (currentSubStep === 2 && thirdCategory) return !thirdPriorityConfidence

    return false
  }

  const createOrgChangeAction = ({
    isHidden,
    priority,
    value,
  }: {
    isHidden?: boolean
    priority: keyof Priorities
    value: string
  }): CustomAction => ({
    callback: (...args: unknown[]) =>
      onSetPriorities({
        ...priorities,
        [priority]: {
          ...priorities[priority],
          updateNotes: args[0] as string,
        },
      }),
    isHidden,
    label:
      'What has changed for your organization in this area since then (Optional)?',
    type: ActionElementType.TEXT_FIELD,
    value,
  })

  const nextActions: ButtonAction[] = [
    // If the user previously had a third (optional) priority and wants to clear it, let them do so
    ...((isLastSubStep &&
      thirdCategory && [
        {
          callback: () =>
            onSetPriorities({
              ...priorities,
              thirdPriority: {
                ...initialPriorities.thirdPriority,
              },
            }),
          label: 'Clear Optional Priority',
          type: ActionElementType.BUTTON,
        } as ButtonAction,
      ]) ||
      []),
    {
      callback: () => setCurrentSubStep(currentSubStep - 1),
      label: 'Previous',
      isHidden: isFirstSubStep,
      type: ActionElementType.BUTTON,
    },
    {
      callback: () =>
        !isLastSubStep ? setCurrentSubStep(currentSubStep + 1) : undefined,
      disabled: checkIfNextIsDisabled(),
      label: 'Next',
      path: isLastSubStep
        ? CheckInWizardStep.YOUR_ORG_MISSION_IMPACT
        : undefined,
      saveUserState: true,
      type: ActionElementType.BUTTON,
    },
  ]

  const firstPriorityTopics: CustomAction[] = firstPriorityGoals.map(
    (goal) => ({
      callback: () =>
        onSetPriorities({
          ...priorities,
          firstPriority: {
            ...firstPriority,
            selectedGoals: onToggleTemplateGoal({
              existingGoals: firstPriority.selectedGoals,
              goal,
            }),
          },
        }),
      checked: firstPriority.selectedGoals.some(
        (selectedGoal) => selectedGoal.label === goal.label
      ),
      label: goal.label,
      type: ActionElementType.CHECKBOX,
      value: goal.templateLibraryId,
    })
  )

  const secondPriorityTopics: CustomAction[] = secondPriorityGoals.map(
    (goal) => ({
      callback: () =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            selectedGoals: onToggleTemplateGoal({
              existingGoals: secondPriority.selectedGoals,
              goal,
            }),
          },
        }),
      checked: secondPriority.selectedGoals.some(
        (selectedGoal) => selectedGoal.label === goal.label
      ),
      label: goal.label,
      type: ActionElementType.CHECKBOX,
      value: goal.templateLibraryId,
    })
  )

  const thirdPriorityTopics: CustomAction[] = thirdPriorityGoals.map(
    (goal) => ({
      callback: () =>
        onSetPriorities({
          ...priorities,
          thirdPriority: {
            ...thirdPriority,
            selectedGoals: onToggleTemplateGoal({
              existingGoals: thirdPriority.selectedGoals,
              goal,
            }),
          },
        }),
      checked: thirdPriority.selectedGoals.some(
        (selectedGoal) => selectedGoal.label === goal.label
      ),
      label: goal.label,
      type: ActionElementType.CHECKBOX,
      value: goal.templateLibraryId,
    })
  )

  const missingPriorityUserActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) => {
        onSetPriorities({
          ...priorities,
          firstPriority: {
            category: args[0] as GoalCategory,
            confidenceLevel: '',
            challenges: firstPriority.challenges || '',
            customPriorities: missingPriority.customPriorities || '',
            selectedGoals: [],
            updateNotes: missingPriority.updateNotes || '',
          },
        })
      },
      label: 'Choose your first priority',
      options: priorityCategoryOptions,
      type: ActionElementType.SELECT,
      value: firstCategory,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...priorities.missingPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${snakeCaseToSpaces(
        missingPriorityCategory,
        true
      )}?`,
      isHidden:
        !missingPriorityCategory ||
        missingPriorityCategory === GoalCategory.None,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: missingPriorityConfidence,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...missingPriority,
            // Ensure that the optional priority input is set to Other if the user is entering a custom priority
            category: missingPriority.category || GoalCategory.Other,
            customPriorities: sanitize(args[0] as string),
          },
        }),
      label: `If your priorities are not listed above, add them here`,
      type: ActionElementType.TEXT_FIELD,
      value: missingPriorityCustomPriorities || '',
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...missingPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      label: `What is your organization’s main challenge regarding this area (Optional)?`,
      type: ActionElementType.TEXT_FIELD,
      value: missingPriorityChallenges,
    },
    createOrgChangeAction({
      priority: 'missingPriority',
      value: missingPriority.updateNotes,
    }),
  ]

  // Needs Assessment only allowed a single (first) priority when missing priority was selected
  const firstPriorityUserActions: CustomAction[] = missingPriorityCategory
    ? missingPriorityUserActions
    : [
        {
          callback: (...args: unknown[]) => {
            onSetPriorities({
              ...priorities,
              firstPriority: {
                category: args[0] as GoalCategory,
                challenges: '',
                confidenceLevel: '',
                customPriorities: '',
                selectedGoals: [],
                updateNotes: '',
              },
            })
          },
          header: 'First priority',
          label: 'Choose your first priority',
          options: priorityCategoryOptions,
          required: true,
          type: ActionElementType.SELECT,
          value: firstCategory,
        },
        ...firstPriorityTopics,
        {
          callback: (...args: unknown[]) =>
            onSetPriorities({
              ...priorities,
              firstPriority: {
                ...priorities.firstPriority,
                confidenceLevel: args[0] as ConfidenceLevel,
              },
            }),
          label: `From 1 to 5, how would you rate your organization’s knowledge in ${firstCategoryLabel}?`,
          isHidden: !firstCategory || firstCategory === GoalCategory.None,
          options: confidenceLevels,
          required: true,
          type: ActionElementType.SELECT,
          value: firstPriorityConfidence,
        },
        {
          callback: (...args: unknown[]) =>
            onSetPriorities({
              ...priorities,
              firstPriority: {
                ...priorities.firstPriority,
                challenges: sanitize(args[0] as string),
              },
            }),
          label: `What would you say is your organization’s main challenge in ${firstCategoryLabel} (Optional)?`,
          isHidden: !firstCategory || firstCategory === GoalCategory.None,
          type: ActionElementType.TEXT_FIELD,
          value: firstPriorityChallenges,
        },
        createOrgChangeAction({
          priority: 'firstPriority',
          value: firstPriorityUpdateNotes,
        }),
      ]

  const secondPriorityUserActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) => {
        onSetPriorities({
          ...priorities,
          secondPriority: {
            category: args[0] as GoalCategory,
            challenges: '',
            confidenceLevel: '',
            customPriorities: '',
            selectedGoals: [],
            updateNotes: '',
          },
        })
      },
      header: 'Second priority',
      label: 'Choose your second priority',
      options: priorityCategoryOptions.filter(
        (p) => p.value !== firstCategory && p.value !== GoalCategory.Other
      ),
      required: true,
      type: ActionElementType.SELECT,
      value: secondPriority.category,
    },
    ...secondPriorityTopics,
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      isHidden: !secondCategory,
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${secondCategoryLabel}?`,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: secondPriorityConfidence,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          secondPriority: {
            ...secondPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      isHidden: !secondCategory,
      label: `What would you say is your organization’s main challenge in ${secondCategoryLabel} (Optional)?`,
      type: ActionElementType.TEXT_FIELD,
      value: secondPriorityChallenges,
    },
    createOrgChangeAction({
      priority: 'secondPriority',
      value: secondPriorityUpdateNotes,
    }),
  ]

  const thirdPriorityUserActions: CustomAction[] = [
    ...(thirdPriority && [
      {
        callback: (...args: unknown[]) => {
          onSetPriorities({
            ...priorities,
            thirdPriority: {
              category: args[0] as GoalCategory,
              challenges: '',
              confidenceLevel: '',
              customPriorities: '',
              selectedGoals: [],
              updateNotes: '',
            },
          })
        },
        header: 'Third priority',
        label: 'Choose your third priority',
        options: priorityCategoryOptions.filter(
          (p) =>
            p.value !== firstCategory &&
            p.value !== secondCategory &&
            p.value !== GoalCategory.Other
        ),
        type: ActionElementType.SELECT,
        value: thirdPriority.category,
      } as CustomAction,
    ]),
    ...(thirdPriority && thirdPriorityTopics),
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          thirdPriority: {
            ...thirdPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      isHidden: !thirdCategory,
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${thirdCategoryLabel}?`,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: thirdPriorityConfidence,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          thirdPriority: {
            ...thirdPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      isHidden: !thirdCategory,
      label: `What would you say is your organization’s main challenge in ${thirdCategoryLabel} (Optional)?`,
      type: ActionElementType.TEXT_FIELD,
      value: thirdPriorityChallenges,
    },
    createOrgChangeAction({
      isHidden: !thirdCategory,
      priority: 'thirdPriority',
      value: thirdPriorityUpdateNotes,
    }),
  ]

  const allUserActions = [
    firstPriorityUserActions,
    secondPriorityUserActions,
    thirdPriorityUserActions,
  ]

  const userActions = allUserActions[currentSubStep]

  return (
    <StepContainer>
      <CheckInSurveyStepper step={0} />
      <Title>Revisit your priorities</Title>
      <Description>Where are you now?</Description>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
